import {
  ChevronUpIcon,
  EnvelopeIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';
import { AnimatePresence, motion } from 'framer-motion';
import type { ReactNode } from 'react';
import { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { twMerge } from 'tailwind-merge';

import { TextStyle } from '../../../DesignSystem/Feedback/TextStyle';
import { FilterChip } from '../../../DesignSystem/Inputs/Chip';
import { TextField } from '../../../DesignSystem/Inputs/TextField';
import { VariableSizeVirtualisedList } from '../../Shared/VirtualisedList';
import { CriteriaItemLoader } from './CriteriaItem';

export type Rule<T> = {
  id: string;
  data: T;
};

export type CustomRule = {
  id: string;
  label: string;
};

export type SearchableRulesDropdownProps<T> = {
  rules: Rule<T>[];
  renderRule: (rule: Rule<T>) => ReactNode;
  renderRulesLoader: () => ReactNode;
  customRules: CustomRule[];
  onRuleSelect: (rule: Rule<T>) => void;
  onCustomRuleSelect: (rule: CustomRule) => void;
  hasInvitePermission?: boolean;
  checkIfCurrentEmailCanBeInvited: (email: string) => Promise<boolean>;
  onMemberSearch: (searchTerm: string) => Promise<void> | void;
  isLoading?: boolean;
  toggleDropdownLabel?: string;
  hasMoreMembersToFetch: boolean;
  fetchMoreMembers: () => void;
  searchTerm: string;
  triggerClassName?: string;
  contentClassName?: string;
  isFetchingMoreMembers?: boolean;
  handleOnEmailInvite: (email: string) => void;
  hasUserInvitePermission: boolean;
};

const messages = defineMessages({
  search: {
    defaultMessage: 'Search',
    id: 'xmcVZ0',
  },
  addRule: {
    defaultMessage: 'Add a rule:',
    id: '/R4vHa',
  },
  addPeopleOrRules: {
    defaultMessage: 'Add people or rules...',
    id: 'aP909H',
  },
  inviteBannerText: {
    defaultMessage:
      'Don’t see who you’re looking for? Type an email to invite a new teammate.',
    id: 'aKlxke',
  },
  addEmail: {
    defaultMessage: 'Add “{email}” to this collection',
    id: 'xqYAfL',
  },
  noResults: {
    defaultMessage: 'No results for this search.',
    id: 'DeCDPs',
  },
});

export function SearchableRulesDropdown<T>(
  props: SearchableRulesDropdownProps<T>
) {
  const {
    rules,
    isLoading = false,
    customRules,
    hasInvitePermission,
    toggleDropdownLabel,
    hasMoreMembersToFetch,
    searchTerm,
    renderRule,
    onRuleSelect,
    onMemberSearch,
    fetchMoreMembers,
    renderRulesLoader,
    onCustomRuleSelect,
    checkIfCurrentEmailCanBeInvited,
    triggerClassName,
    contentClassName,
    isFetchingMoreMembers,
    handleOnEmailInvite,
    hasUserInvitePermission,
  } = props;

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [canBeInvited, setCanBeInvited] = useState<boolean>(false);

  const checkInvitation = useCallback(
    async (email: string) => {
      const result =
        (await checkIfCurrentEmailCanBeInvited(email)) && hasInvitePermission;
      setCanBeInvited(result ?? false);
    },
    [checkIfCurrentEmailCanBeInvited, hasInvitePermission]
  );

  const handleDropdownOpen = () => {
    document.getElementById('searchableDropdown')?.focus();
    setIsOpen(true);
  };

  const handleDropdownClose = () => {
    setIsOpen(false);
    document.getElementById('searchableDropdown')?.blur();
    onMemberSearch('');
  };

  const handleSelectionChange = (selectedRule: Rule<T>) => {
    const currentRule = rules.find((rule) => rule.id === selectedRule.id);
    if (currentRule) {
      onRuleSelect(currentRule);
    }
    setTimeout(() => {
      handleDropdownClose();
    }, 0);
  };

  const toggleDropdown = () => {
    if (isOpen) {
      handleDropdownClose();
    } else {
      handleDropdownOpen();
    }
  };

  const handleOnInputBlur = () => {
    setTimeout(() => {
      if (
        !document.activeElement?.id.includes('categoryChip') &&
        !document.activeElement?.id.includes('dropdownOption') &&
        !document.activeElement?.id.includes('toggleDropdown')
      ) {
        handleDropdownClose();
      }
    }, 0);
  };

  const handleOnToggleBlur = () => {
    setTimeout(() => {
      if (
        !document.activeElement?.id.includes('categoryChip') &&
        !document.activeElement?.id.includes('dropdownOption') &&
        !document.activeElement?.id.includes('searchableDropdown')
      ) {
        handleDropdownClose();
      }
    }, 0);
  };

  const handleOnInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      handleDropdownClose();
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      handleDropdownOpen();
    }
  };

  const handleOnInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isOpen) {
      handleDropdownOpen();
    }
    const searchTerm = (e.target as HTMLInputElement).value;
    checkInvitation(searchTerm);
    onMemberSearch(searchTerm);
  };

  const handleCustomRuleSelection = (rule: CustomRule) => {
    setIsOpen(false);
    onCustomRuleSelect(rule);
  };

  return (
    <div className="w-full">
      <div className="relative h-11">
        <div
          className={twMerge(
            'flex w-full flex-col items-start justify-start rounded-lg border border-gray-5 bg-gray-1 py-1',
            isOpen && 'border-primary-6 ring-1 ring-primary-6',
            !isOpen && 'hover:border-primary-6',
            triggerClassName
          )}
        >
          <SearchInput
            id="searchableDropdown"
            value={searchTerm}
            handleSearchInputChange={handleOnInputChange}
            handleSearchInputKeyDown={handleOnInputKeyDown}
            handleSearchInputBlur={handleOnInputBlur}
            handleSearchInputClick={handleDropdownOpen}
          />
          <ToggleButton
            isOpen={isOpen}
            toggleDropdown={toggleDropdown}
            onBlur={handleOnToggleBlur}
            toggleDropdownLabel={toggleDropdownLabel}
          />
        </div>
      </div>
      {isOpen && (
        <div
          className={twMerge(
            'z-1000 absolute top-[var(--trigger-height)] w-[var(--trigger-width)] rounded-lg bg-gray-1 shadow-md-down ring-1 ring-gray-10 ring-opacity-5',
            contentClassName
          )}
          style={{ top: `calc(var(--trigger-height) + 10px)`, zIndex: 100 }}
          data-testid="searchableDropdownPopover"
        >
          <>
            {isLoading ? (
              renderRulesLoader()
            ) : (
              <>
                {canBeInvited && rules.length === 0 ? (
                  <button
                    onClick={() => {
                      handleOnEmailInvite(searchTerm);
                      setTimeout(() => {
                        handleDropdownClose();
                      }, 0);
                    }}
                    className="flex w-full cursor-pointer items-start rounded-lg bg-gray-1 hover:bg-gray-3 focus:bg-gray-3 focus:outline-none"
                    id={`dropdownOption-${searchTerm}`}
                  >
                    <AddEmailBanner email={searchTerm} />
                  </button>
                ) : (
                  <>
                    <RulesList
                      rules={rules}
                      customRules={customRules}
                      hasInvitePermission={hasUserInvitePermission}
                      handleRuleSelection={handleSelectionChange}
                      handleCustomRuleSelection={handleCustomRuleSelection}
                      renderRule={renderRule}
                      handleDropdownBlur={handleOnInputBlur}
                      hasMoreMembersToFetch={hasMoreMembersToFetch}
                      fetchMoreMembers={fetchMoreMembers}
                      isFetchingMoreMembers={isFetchingMoreMembers}
                    />
                    {hasUserInvitePermission && rules.length > 0 && (
                      <InviteBanner />
                    )}
                  </>
                )}
              </>
            )}
          </>
        </div>
      )}
    </div>
  );
}

function SearchInput({
  value,
  id,
  handleSearchInputChange,
  handleSearchInputKeyDown,
  handleSearchInputBlur,
  handleSearchInputClick,
}: {
  id: string;
  value: string;
  handleSearchInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSearchInputKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  handleSearchInputBlur: () => void;
  handleSearchInputClick: () => void;
}) {
  const { formatMessage } = useIntl();
  return (
    <div className="min-w-[100%]">
      <TextField
        shouldUseReactAriaInput
        autoComplete="off"
        hideLabel
        inputClassName="text-ellipsis border-none text-sm py-0 px-3 h-[30px] focus:outline-none focus:ring-0"
        inputSize="md"
        invalidText=""
        isInvalid={false}
        label={formatMessage(messages.search)}
        onChange={handleSearchInputChange}
        onClick={handleSearchInputClick}
        onKeyDown={(e) => handleSearchInputKeyDown(e)}
        onBlur={() => handleSearchInputBlur()}
        value={value}
        placeholder={formatMessage(messages.addPeopleOrRules)}
        id={id}
      />
    </div>
  );
}

function ToggleButton({
  isOpen,
  toggleDropdown,
  toggleDropdownLabel,
  onBlur,
}: {
  isOpen: boolean;
  toggleDropdown: () => void;
  onBlur: () => void;
  toggleDropdownLabel?: string;
}) {
  const { formatMessage } = useIntl();
  return (
    <AnimatePresence>
      <button
        aria-label={toggleDropdownLabel}
        onClick={toggleDropdown}
        className="items-top absolute right-3 top-0 flex h-full"
        id="toggleDropdown"
        onBlur={onBlur}
        tabIndex={0}
      >
        <div className="sr-only">
          {formatMessage(messages.addPeopleOrRules)}
        </div>
        <motion.div
          className="mt-3 flex w-4"
          animate={isOpen ? 'opened' : 'closed'}
          variants={{
            opened: { rotate: 0 },
            closed: { rotate: -180 },
          }}
          transition={{ duration: 0.3 }}
        >
          <ChevronUpIcon className="h-4 w-4 stroke-[2px] text-gray-9" />
        </motion.div>
      </button>
    </AnimatePresence>
  );
}

function AddEmailBanner({ email }: { email: string }) {
  const { formatMessage } = useIntl();
  return (
    <div className="inline-flex h-10 w-full items-start justify-start gap-2 rounded-lg px-5 py-3">
      <EnvelopeIcon className="relative h-4 w-4" />
      <TextStyle
        variant="sm-regular"
        className="shrink grow basis-0 rounded text-start leading-snug text-gray-8"
      >
        {formatMessage(messages.addEmail, { email })}
      </TextStyle>
    </div>
  );
}

function RulesList<T>({
  rules,
  customRules,
  hasInvitePermission,
  handleRuleSelection,
  handleCustomRuleSelection,
  renderRule,
  handleDropdownBlur,
  hasMoreMembersToFetch,
  fetchMoreMembers,
  isFetchingMoreMembers,
}: {
  rules: Rule<T>[];
  customRules: CustomRule[];
  hasInvitePermission?: boolean;
  handleRuleSelection: (rule: Rule<T>) => void;
  handleCustomRuleSelection: (rule: CustomRule) => void;
  renderRule: (rule: Rule<T>) => ReactNode;
  handleDropdownBlur: () => void;
  hasMoreMembersToFetch: boolean;
  fetchMoreMembers: () => void;
  isFetchingMoreMembers?: boolean;
}) {
  const { formatMessage } = useIntl();

  const itemCount =
    hasMoreMembersToFetch && rules.length ? rules.length + 1 : rules.length;

  const renderItem = useCallback(
    (rule: Rule<T>, index?: number) => {
      if (index === 0) {
        return (
          <>
            <div className="flex h-[78px] max-w-full flex-col items-start justify-start gap-1 border-b border-gray-5 bg-gray-1 pt-2">
              <div className="px-4 text-sm font-medium leading-snug text-gray-9">
                {formatMessage(messages.addRule)}
              </div>
              <div className="flex w-full items-start justify-start gap-2 overflow-x-scroll pb-3">
                {customRules.map((rule, index) => (
                  <FilterChip
                    key={rule.id}
                    id={`categoryChip-${rule.id}`}
                    data-testid={`categoryChip-${rule.id}`}
                    onClick={() => handleCustomRuleSelection(rule)}
                    className={twMerge(
                      index === 0 && 'ml-4',
                      index === customRules.length - 1 && 'mr-4'
                    )}
                  >
                    {rule.label}
                  </FilterChip>
                ))}
              </div>
            </div>
            <button
              key={rule.id}
              className="flex w-full cursor-pointer items-start bg-gray-1 px-4 py-2 hover:bg-gray-3 focus:bg-gray-3 focus:outline-none"
              id={`dropdownOption-${rule.id}`}
              onClick={() => handleRuleSelection(rule)}
            >
              {renderRule(rule)}
            </button>
          </>
        );
      }
      if ((index && index >= rules.length) || !rule.id) {
        return null;
      }

      return (
        <>
          <button
            key={rule.id}
            className="flex w-full cursor-pointer items-start bg-gray-1 px-4 py-2 hover:bg-gray-3 focus:bg-gray-3 focus:outline-none"
            id={`dropdownOption-${rule.id}`}
            onClick={() => handleRuleSelection(rule)}
          >
            {renderRule(rule)}
          </button>
          {isFetchingMoreMembers && index === rules.length - 1 && (
            <div className="px-4">
              <CriteriaItemLoader showDropdownLoader={false} />
            </div>
          )}
        </>
      );
    },
    [
      customRules,
      formatMessage,
      handleCustomRuleSelection,
      handleRuleSelection,
      isFetchingMoreMembers,
      renderRule,
      rules.length,
    ]
  );

  if (rules.length === 0) {
    return (
      <div className="py-1">
        <TextStyle
          variant="sm-regular"
          className="shrink grow basis-0 rounded px-5 py-2 leading-snug text-gray-8"
        >
          {formatMessage(messages.noResults)}
        </TextStyle>
      </div>
    );
  }

  return (
    <div
      className={twMerge(
        'relative rounded-lg py-1',
        hasInvitePermission && 'pb-[40px]'
      )}
      role="listbox"
      onBlur={handleDropdownBlur}
    >
      <section
        className="min-h-[200px] overflow-scroll"
        style={{
          height: '200px',
        }}
      >
        <VariableSizeVirtualisedList
          itemSize={(index) => (index === 0 ? 146 : 68)}
          estimatedItemSize={68}
          itemCount={itemCount}
          items={rules}
          onLoadMore={fetchMoreMembers}
          renderItem={renderItem}
        />
      </section>
    </div>
  );
}

function InviteBanner() {
  const { formatMessage } = useIntl();
  return (
    <div className="absolute bottom-0 inline-flex h-[42px] w-full items-center justify-start gap-2 rounded-b-lg border-t border-gray-4 bg-gray-1 px-5 pb-2 pt-3">
      <UserPlusIcon className="relative h-4 w-4" />
      <TextStyle
        variant="sm-regular"
        className="shrink grow basis-0 font-normal leading-snug text-gray-8"
      >
        {formatMessage(messages.inviteBannerText)}
      </TextStyle>
    </div>
  );
}
